import {IRegPointer, IUiEnv} from "/@lib@/commons/registry.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IExecutorInUniversePointer, IJobBuilderActionContext, JJob, JobBuilderAction, UiJobFactory} from "/@lib@/core/executor.js";
import {Dataset, InputArea, InputChoiceArea, SelectArea} from "/@lib@/commons/areas.js";
import {LANG} from "/@lib@/commons/lang.js";


/** RelaunchCidTask de base générique */
export class RelaunchCidTaskBaseJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends UiJobFactory<C, IJobBuilderActionContext> {
	constructor(id: string) {
		super();
		this.setUiJobSng([id]);
		this.setLabel(id);
		this.setJobFactoryCode("relaunchCidTasks");
		this.setJobBuilder(new JobBuilderAction(this)
			.addJobDataValidity(async (props, ctx) => {
				if (this._processings && this._processings.length > 0) {
					if (!props.preconditions || props.preconditions === "")
						return false;
				}
				return true;
			})
			.addJobDataBuilder('preconditions', async (datasPending, jobsParameters, ctx) => {
				// - preconditions
				if (jobsParameters._processings) {
					datasPending.preconditions = "";
					jobsParameters._processings.forEach((code: string, offset: number) => {
						if (offset > 0)
							datasPending.preconditions += "|";
						datasPending.preconditions += "(persistMeta(processing," + code + ")";
						let processing = this.getProcessings().find(entry => entry.code == code);
						if (!processing.isVcb || jobsParameters._rebuildAllVersions !== 'all')
							datasPending.preconditions += " &headInUrlTree";
						datasPending.preconditions += ")";
					});
				}
				// Clean
				datasPending._processings = datasPending._rebuildAllVersions = undefined;
			}, 10)
			.addJobDialogAreas([(ctx) => {
				if (this.getProcessings().length > 0) {
					let processindsDataSet: Dataset<any> = {};
					this.getProcessings().sort((prA, prB) => prA.name.localeCompare(prB.name)).forEach((proc) => processindsDataSet[proc.code] = {label: proc.name + (proc.isVcb ? "*" : "")});
					return new SelectArea("_processings")
						.setMultiple(true)
						.setLabel("Types de ressources")
						.setDataset(processindsDataSet)
						.setRequired(true)
				}
			},
				(ctx) => {
					if (this.getChoiceRebuildVersions()) {
						return new InputChoiceArea("_rebuildAllVersions")
							.setLabel("Versions des ressources")
							.setDataset({mostRecent: {label: "La plus récente uniquement", disabled: false}, all: {label: "Toutes (si disponibles : *)"}})
							.setDefaultValue('mostRecent')
					}
				}
			]))
	}

	/** Choix des processings */
	protected _processings: { code: string, name: string, isVcb: boolean }[] = [];

	addChoiceProcessing(code: string, name: string, isVcb: boolean): this {
		this._processings.push({code, name, isVcb});
		return this;
	}

	getProcessings(): { code: string, name: string, isVcb: boolean }[] {
		return this._processings;
	}

	/** Choix de rebuild des versions (si vcb) */
	protected _choiceRebuildVersions: boolean = false;

	addChoiceRebuildVersions(choice: boolean) {
		this._choiceRebuildVersions = choice;
	}

	getChoiceRebuildVersions(): boolean {
		return this._choiceRebuildVersions;
	}

	async buildLongDescForJob(ctx: C, job: JJob, target: HTMLElement): Promise<void> {
		if (job && job.jobProps) {
			const uiProps = job.jobProps[JobBuilderAction.UI_PROPS];
			let list = target.appendChild(<ul/>);
			if (job.jobProps[JobBuilderAction.UI_PROPS]) {
				// - processings
				if (uiProps._processings) {
					let processingsStr: string[] = [];
					uiProps._processings.forEach((proc: string) => {
						const procObj = this.getProcessings().find((entry) => entry.code === proc);
						processingsStr.push(procObj ? procObj.name : proc);
					});
					if (processingsStr.length > 0) {
						processingsStr = processingsStr.sort();
						const processings = processingsStr.join(", ");
						const str = `Traitement(s) : ${processings}`;
						list.appendChild(<li>{str}</li>);
					}
				}
				// - choiceRebuildVersions
				if (this.getChoiceRebuildVersions()) {
					let rbv = uiProps._rebuildAllVersions === 'all' ? "oui" : "non";
					const str = `Traitement des versions antérieures (si pertinent) : ${rbv}`;
					list.appendChild(<li>{str}</li>);
				}
			}
			// - updateRawContent/scarMigrate
			if (job.jobProps.updateRawContent?.action == 'scarMigrate') {
				const wspTypes = job.jobProps.updateRawContent.wspTypes;
				if (wspTypes) {
					wspTypes.forEach((migrRule: any) => {
						if (migrRule.matchKey == '.*') {
							list.appendChild(<li>Migration des versions mediums et mineures</li>);
						} else {
							const from = migrRule.matchKey;
							const to = migrRule.renameTo;
							let str = `Migration de '${from}' vers '${to}'`;
							list.appendChild(<li>{str}</li>);
							const subList = list.appendChild(<ul/>);
							if (migrRule.remOptions) {
								const exts = migrRule.remOptions.join(', ');
								str = migrRule.remOptions.length > 1 ? `Suppression des extensions '${exts}'` : `Suppression de l'extension '${exts}'`;
								subList.appendChild(<li>{str}</li>);
							}
							if (migrRule.addOptions) {
								const exts = migrRule.addOptions.join(', ');
								str = migrRule.remOptions.length > 1 ? `Ajout des extensions '${exts}'` : `Ajout de l'extension '${exts}'`;
								subList.appendChild(<li>{str}</li>);
							}
							if (migrRule.options) {
								let extsArray: string[] = [];
								migrRule.options.forEach((opt: any) => {
									extsArray.push(opt.matchKey);
								});
								const exts = extsArray.join(', ');
								str = extsArray.length > 1 ? `Migration des extensions '${exts}'` : `Migration de l'extension '${exts}'`;
								subList.appendChild(<li>{str}</li>);
							}
						}
					});
				}
			}
		}
	}
}

/** Suppression des ressources en corbeille */
export class RemoveTrashedJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends RelaunchCidTaskBaseJobFactory<C> {
	static SINGLETON = new RemoveTrashedJobFactory();

	constructor(id: string = "removeTrashedJob") {
		super(id);
		this.setLabel("Suppression des documents marqués 'invisibles'");
		// FIXME rôle dédié coté univers et ici
		const jobBuilder = this.getJobBuilder();
		jobBuilder.setPlanned("byUser")
			.addBaseJobDatas({
					preconditions: "headInUrlTree&persistMeta(trashed,true)",
					stopAfterFailures: 50,
					updatePersistMetas: [
						{action: "replace", key: "action", value: "remove"}
					]
				}
			);
	}
}

/** Clean de l'index ES */
export class RebuildEsIndexJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends RelaunchCidTaskBaseJobFactory<C> {

	constructor(id: string = "rebuildEsIndexJob") {
		super(id);
		this.setLabel("Réindexation Elasticsearch");
		// FIXME rôle dédié coté univers et ici
		const jobBuilder = this.getJobBuilder();
		jobBuilder.setPlanned("byUser")
			.addBaseJobDatas({
					stopAfterFailures: 50,
					forceContentIndex: true,
					extractMetasFromContent: true,
				}
			);
	};

}

/** Reconstruction des vues */
export class RemakeViewsJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends RelaunchCidTaskBaseJobFactory<C> {
	constructor(id: string = "remakeViewsJob") {
		super(id);
		this.setLabel("Reconstruction des vues des documents");
		this.setDescription("Reconstruction en masse des vues des documents sélectionnés");
		// FIXME rôle dédié coté univers et ici
		const jobBuilder = this.getJobBuilder();
		jobBuilder.setPlanned("byUser")
			.addBaseJobDatas({
				stopAfterFailures: 50,
				overrideViews: [
					{redirectToPrevious: true,}
				],
				forceContentIndex: true,
				extractMetasFromContent: true,
				postconditions: "atLeastOneViewForced",
				updatePersistMetas: [
					{action: "translate", key: "state", dict: {failure: "new"}}
				]
			})
			.addJobDialogAreas([(ctx) => {
				let viewsDataSet: Dataset<any> = {};
				viewsDataSet["~.*"] = {label: "Toutes les vues"};
				viewsDataSet["~news"] = {label: "Nouvelles vues uniquement"};
				this._views.forEach((view) => {
					viewsDataSet[view.selector.source] = {label: view.name}
				});
				return new SelectArea("_views")
					.setMultiple(true)
					.setLabel("Vues")
					.setDefaultValue(["~new"])
					.setDataset(viewsDataSet)
					.setRequired(true);
			}])
			.addJobDataBuilder('overrideViews', async (datasPending, jobsParameters, ctx) => {
				// - overrideViews
				datasPending.overrideViews = [];
				if (jobsParameters._views.includes("~.*")) {
					datasPending.overrideViews.push({
						matchCodeView: ".*",
						forceRemakeInMetas: true,
						builtBefore: Date.now()
					});
				} else if (jobsParameters._views.includes("~news")) {
					datasPending.overrideViews.push({
						matchCodeView: ".*",
						redirectToPrevious: true,
					});
				} else {
					datasPending.overrideViews.push({
						matchCodeView: '^(' + jobsParameters._views.join('|') + ')$',
						forceRemakeInMetas: true,
						builtBefore: Date.now()
					});
				}
				// clean
				datasPending._views = undefined;
			}, 10)
	};

	/** Choix des views à rebuilder */
	protected _views: { selector: RegExp, name: string }[] = [];

	addChoiceView(name: string, code: string | RegExp) {
		let selector: RegExp;
		if (typeof code === "string")
			selector = new RegExp(LANG.escape4Regexp(code));
		else
			selector = code;
		this._views.push({selector, name});
	}

	async buildLongDescForJob(ctx: C, job: JJob, target: HTMLElement): Promise<void> {
		await super.buildLongDescForJob(ctx, job, target);
		if (job && job.jobProps && job.jobProps[JobBuilderAction.UI_PROPS]) {
			const uiProps = job.jobProps[JobBuilderAction.UI_PROPS];
			let list = target.appendChild(<ul/>);
			if (uiProps._views) {
				let viewsStr: string[] = [];
				if (uiProps._views.includes("~.*")) {
					viewsStr.push("toutes");
				} else if (uiProps._views.includes("~news")) {
					viewsStr.push("nouvelles uniquement");
				} else {
					uiProps._views.forEach((selectorStr: string) => {
						const viewObj = this._views.find((entry) => entry.selector.source === selectorStr);
						viewsStr.push(viewObj ? viewObj.name : selectorStr);
					});
				}
				if (viewsStr.length > 0) {
					viewsStr = viewsStr.sort();
					const views = viewsStr.join(", ");
					const str = `Vue(s) : ${views}`;
					list.appendChild(<li>{str}</li>);
				}
			}
		}
	}

}

/** Reconstruction de type updateRawContent=oldRawContentAsNew */
export class ContentAsNewJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends RelaunchCidTaskBaseJobFactory<C> {
	constructor(id: string = "remakeAllAsNewContentJob") {
		super(id);
		this.setLabel("Reconstruction intégrale des documents");
		this.setDescription("Reconstruction intégrale des documents, comme si son contenu avait été ré-importé");

		const jobBuilder = this.getJobBuilder();
		jobBuilder.setPlanned("byUser")
			.addBaseJobDatas({
				stopAfterFailures: 50,
				updateRawContent: {action: "oldRawContentAsNew"},
			});
	};

}

/** RelaunchCidTask de base générique */
export class ReindexActLogsJobFactory<C extends IRegPointer<IExecutorInUniversePointer & IUiEnv>> extends UiJobFactory<C, IJobBuilderActionContext> {
	constructor(id: string) {
		super();
		this.setUiJobSng([id]);
		this.setLabel(id);
		this.setJobFactoryCode("reindexActLogs");
		this.setJobBuilder(new JobBuilderAction(this)
			.addJobDataValidity(async (props, ctx) => {return true;})
			.addJobDialogAreas([(ctx) => {
				return new InputArea("from").setInputType("date").setLabel("Depuis").setRequired(false);
			},
				(ctx) => {
					return new InputArea("to").setInputType("date").setLabel("Au").setRequired(false);
				}
			]))
	}
}